/*
 * Copyright (C) 2009-2025 SonarSource Sàrl
 * All rights reserved
 * mailto:info AT sonarsource DOT com
 */

import {
  Checkbox,
  Form,
  IconCheck,
  IconError,
  IconWarning,
  Text,
  TextSize,
} from '@sonarsource/echoes-react';
import classNames from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';
import { Fragment } from 'react/jsx-runtime';
import { PopupZLevel, SearchSelectDropdown } from '~design-system';
import { SoftwareQuality } from '~shared/types/clean-code-taxonomy';
import { IssueType } from '~sq-server-commons/types/issues';

interface QualitySelectorItem<TMain, TSeverity> {
  main: TMain;
  severities: TSeverity[];
}

interface QualitySelectorConfig<TMain, TSeverity> {
  description: string;
  getControlLabel: (severities: TSeverity[], formatMessage: (id: string) => string) => string;
  getMainLabel: (main: TMain) => string;
  invalidTextKey?: string;
  mainOptions: TMain[];
  severityOptions: Array<{ label: string; value: TSeverity }>;
  title: string;
}

interface GenericQualitySelectorProps<TMain, TSeverity> {
  config: QualitySelectorConfig<TMain, TSeverity>;
  hasAddedCriticalTypes: boolean;
  onChange: (items: Array<QualitySelectorItem<TMain, TSeverity>>) => void;
  selectedItems: Array<QualitySelectorItem<TMain, TSeverity>>;
}

export function GenericQualitySelector<TMain, TSeverity>({
  config,
  selectedItems,
  onChange,
  hasAddedCriticalTypes,
}: Readonly<GenericQualitySelectorProps<TMain, TSeverity>>) {
  const intl = useIntl();

  const handleCheckboxChange = (checked: boolean | string, mainValue: TMain) => {
    if (typeof checked !== 'boolean') {
      return;
    }

    let newItems = selectedItems;
    if (checked) {
      newItems = [...newItems, { main: mainValue, severities: [] }];
    } else {
      newItems = newItems.filter((item) => item.main !== mainValue);
    }

    onChange(newItems);
  };

  const handleSelectChange = (severity: TSeverity, mainValue: TMain) => {
    const item = selectedItems.find((item) => item.main === mainValue);

    if (item) {
      if (item.severities.includes(severity)) {
        item.severities = item.severities.filter((s) => s !== severity);
      } else {
        item.severities = [...item.severities, severity];
      }
    }

    onChange([...selectedItems]);
  };

  const getControlLabel = (mainValue: TMain) => {
    const item = selectedItems.find((item) => item.main === mainValue);
    return item?.severities.length
      ? config.getControlLabel(item.severities, (id) => intl.formatMessage({ id }))
      : intl.formatMessage({ id: 'settings.sandbox_issues.choose_severities' });
  };

  return (
    <Form.Section
      className="sw-mt-6 sw-w-full sw-flex sw-flex-col sw-gap-3"
      description={
        <Text as="p" className="-sw-mt-1" isSubtle size={TextSize.Small}>
          <FormattedMessage id={config.description} />
        </Text>
      }
      title={
        <Text isHighlighted>
          <FormattedMessage
            id={config.title}
            values={{
              required: <Text colorOverride="echoes-color-text-danger">*</Text>,
            }}
          />
        </Text>
      }
      titleAs="h5"
    >
      {config.mainOptions.map((mainValue) => {
        const item = selectedItems.find((item) => item.main === mainValue);

        return (
          <Fragment key={String(mainValue)}>
            <div className="sw-flex sw-items-center sw-justify-between">
              <div className="sw-min-h-900 sw-items-center sw-flex">
                <Checkbox
                  checked={Boolean(item)}
                  label={config.getMainLabel(mainValue)}
                  onCheck={(checked) => {
                    handleCheckboxChange(checked, mainValue);
                  }}
                />
              </div>

              {item && (
                <div className="sw-relative">
                  <SearchSelectDropdown
                    closeMenuOnSelect={false}
                    controlAriaLabel={intl.formatMessage(
                      { id: 'settings.sandbox_issues.select_severities_for' },
                      { quality: item.main as string },
                    )}
                    controlLabel={getControlLabel(mainValue)}
                    controlSize="large"
                    defaultOptions={config.severityOptions.map((s) => ({
                      value: s.value,
                      label: s.label,
                      Icon: (
                        <IconCheck
                          className={classNames({
                            'sw-invisible': !item?.severities.includes(s.value),
                          })}
                          color="echoes-color-icon-selected"
                        />
                      ),
                    }))}
                    isSearchable={false}
                    onChange={(selected) => {
                      if (selected?.value) {
                        handleSelectChange(selected.value as TSeverity, mainValue);
                      }
                    }}
                    zLevel={PopupZLevel.Absolute}
                  />
                </div>
              )}
            </div>
            {hasAddedCriticalTypes && (
              <QualityWarningMessage mainValue={mainValue} selectedItems={selectedItems} />
            )}
          </Fragment>
        );
      })}
      {config.invalidTextKey && (
        <Text
          as="p"
          className="-sw-mt-2"
          colorOverride="echoes-color-text-danger"
          size={TextSize.Small}
        >
          <IconError className="sw-mr-1" />
          {intl.formatMessage({ id: config.invalidTextKey })}
        </Text>
      )}
    </Form.Section>
  );
}

function QualityWarningMessage<TMain, TSeverity>({
  mainValue,
  selectedItems,
}: Readonly<{
  mainValue: TMain;
  selectedItems: Array<QualitySelectorItem<TMain, TSeverity>>;
}>) {
  const item = selectedItems.find((item) => item.main === mainValue);
  let messageKey: string | undefined;
  if (
    [SoftwareQuality.Reliability, IssueType.Bug].includes(item?.main as SoftwareQuality | IssueType)
  ) {
    messageKey = 'settings.sandbox_issues.qualities.reliability.warning';
  } else if (
    [SoftwareQuality.Security, IssueType.Vulnerability].includes(
      item?.main as SoftwareQuality | IssueType,
    )
  ) {
    messageKey = 'settings.sandbox_issues.qualities.security.warning';
  }

  if (!messageKey) {
    return null;
  }

  return (
    <Text as="p" colorOverride="echoes-color-text-warning" size={TextSize.Small}>
      <IconWarning className="sw-mr-1" />
      <FormattedMessage
        id={messageKey}
        values={{
          b: (text) => (
            <Text colorOverride="echoes-color-text-warning" isHighlighted size={TextSize.Small}>
              {text}
            </Text>
          ),
        }}
      />
    </Text>
  );
}
